<template>
	<Card v-if="visible" :bordered="false" class="paas-list-status">
		<Row :gutter="[24, 12]" align="top">
			<Col flex="1 1 12rem" class="paas-list-status-main">
				<Button
					:type="openButtonType"
					class="paas-list-status-open"
					:class="{ 'new-paas-results': paasStore.newTask }"
					@click="onOpen"
				>
					{{ openButtonLabel }}
				</Button>
				<Row v-if="hasStatusAlerts">
					<Col :span="0" :xl="24">
						<PaasListStatusAlerts :warning="warning" :problemsCount="alertProblemsCount" />
					</Col>
				</Row>
			</Col>
			<Col v-if="showCounts" flex="none" class="paas-list-status-metrics-block">
				<RadioGroup
					class="paas-list-status-metrics"
					:value="listFilterRef"
					button-style="solid"
					size="small"
				>
					<RadioButton
						v-for="metric in metrics"
						:key="metric.key"
						:value="metric.key"
						class="paas-metric-button"
						@click.prevent="onFilterClick(metric.key)"
					>
						<span class="paas-metric-title">
							{{ metric.title }}
							<Tooltip v-if="metric.help" :title="metric.help">
								<QuestionCircleOutlined class="paas-metric-help-icon" @click.stop />
							</Tooltip>
						</span>
						<span class="paas-metric-value">{{ metric.value }}</span>
					</RadioButton>
				</RadioGroup>
				<Button
					v-if="listFilterRef"
					type="link"
					size="small"
					class="paas-list-filter-clear"
					@click="onClearFilter"
				>
					{{ $t('paas.listPanel.clearFilter') }}
				</Button>
			</Col>
			<Col v-if="hasStatusAlerts" :span="24" :xl="0">
				<PaasListStatusAlerts :warning="warning" :problemsCount="alertProblemsCount" />
			</Col>
		</Row>
	</Card>
</template>

<script>
import { computed, onMounted, toRef, watch } from 'vue';
import { Button, Card, Col, Radio, Row, Tooltip } from 'ant-design-vue';
import { QuestionCircleOutlined } from '@ant-design/icons-vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import Consts from '@/api/Consts';
import { useBaseStationsStore } from '@/stores/baseStations';
import { useLicenseStore } from '@/stores/licenseStore';
import { usePaasStore } from '@/stores/paas';

import { getPaasCoordsWarning } from '@/components/paas/paasListStatusUtils';
import PaasListStatusAlerts from '@/components/paas/PaasListStatusAlerts.vue';


export default {
	components: {
		Button,
		Card,
		Col,
		Row,
		RadioGroup: Radio.Group,
		RadioButton: Radio.Button,
		Tooltip,
		QuestionCircleOutlined,
		PaasListStatusAlerts,
	},
	props: {
		listFilter: {
			type: String,
			default: '',
		},
	},
	emits: ['update:listFilter'],
	setup(props, { emit }) {
		const { t } = useI18n();
		const router = useRouter();
		const paasStore = usePaasStore();
		const licenseStore = useLicenseStore();
		const baseStationsStore = useBaseStationsStore();
		const visible = computed(() => licenseStore.allowPaas && baseStationsStore.items.length > 0);
		const showCounts = computed(() => paasStore.showProgress && paasStore.totalCount > 0);
		const openButtonLabel = computed(() => {
			if (paasStore.newTask) {
				return t('paas.newResultsLabel');
			}
			if (paasStore.showProgress && paasStore.finishedCount < paasStore.totalCount) {
				return t('paas.progressHint');
			}
			return t('paas.header');
		});
		const openButtonType = computed(() => (paasStore.newTask ? 'primary' : 'default'));
		const warning = computed(() => getPaasCoordsWarning(
			baseStationsStore.items
				.filter(item => !item.deleted && 'paas_coords_age_days' in item)
				.map(item => item.paas_coords_age_days),
		));
		if (licenseStore.allowPaas) {
			onMounted(() => {
				paasStore.updatePaasData();
			});
		}
		watch(() => licenseStore.allowPaas, value => {
			if (value) {
				paasStore.updatePaasData();
			}
		});
		const problemsCount = computed(() => paasStore.taskProblems.length);
		const alertProblemsCount = computed(() => (showCounts.value ? problemsCount.value : 0));
		const hasStatusAlerts = computed(() => (
			warning.value.kind === 'stale'
			|| warning.value.kind === 'never'
			|| alertProblemsCount.value > 0
		));
		const metrics = computed(() => [
			{ key: 'sentTotal', title: t('paas.listPanel.sentTotal'), value: paasStore.totalCount, help: null },
			{
				key: 'sentWithMeasurements',
				title: t('paas.listPanel.sentWithMeasurements'),
				value: paasStore.sentWithMeasurementsCount,
				help: t('paas.listPanel.sentWithMeasurementsTooltip'),
			},
			{ key: 'solutions', title: t('paas.listPanel.solutions'), value: paasStore.solutionCount, help: null },
			{ key: 'errors', title: t('paas.listPanel.errors'), value: paasStore.errorsCount, help: null },
		]);
		const listFilterRef = toRef(props, 'listFilter');
		const onFilterClick = key => {
			emit('update:listFilter', listFilterRef.value === key ? '' : key);
		};
		const onClearFilter = () => emit('update:listFilter', '');
		watch(showCounts, value => {
			if (!value) {
				onClearFilter();
			}
		});
		const onOpen = () => router.push({ name: 'stats-type', params: { id: 'coordinates-correction' } });
		return {
			visible,
			showCounts,
			openButtonLabel,
			openButtonType,
			warning,
			paasStore,
			alertProblemsCount,
			hasStatusAlerts,
			listFilterRef,
			metrics,
			onFilterClick,
			onClearFilter,
			onOpen,
			colorSuccess: Consts.COLORS.success,
			colorSuccessDark: Consts.COLORS.successDark,
		};
	},
};
</script>

<style scoped>
.paas-list-status :deep(.ant-card-body) {
	padding: 11px 12px 10px;
}

.paas-list-status-main {
	min-width: 0;
}

.paas-list-status-metrics-block {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 4px;
}

.paas-list-status-metrics {
	display: inline-grid;
	grid-template-columns: repeat(2, minmax(9.5rem, 1fr));
	width: max-content;
	max-width: 100%;
}

.paas-list-status-metrics .paas-metric-button.ant-radio-button-wrapper {
	height: auto;
	width: 100%;
	margin: 0;
	line-height: 1.2;
	padding: 4px 12px;
	text-align: center;
	border-left-width: 1px;
	border-radius: 0;
}

.paas-list-status-metrics .paas-metric-button.ant-radio-button-wrapper::before {
	display: none;
}

.paas-list-status-metrics .paas-metric-button.ant-radio-button-wrapper:nth-child(1) {
	border-radius: 2px 0 0 0;
}

.paas-list-status-metrics .paas-metric-button.ant-radio-button-wrapper:nth-child(2) {
	margin-left: -1px;
	border-radius: 0 2px 0 0;
}

.paas-list-status-metrics .paas-metric-button.ant-radio-button-wrapper:nth-child(3) {
	margin-top: -1px;
	border-radius: 0 0 0 2px;
}

.paas-list-status-metrics .paas-metric-button.ant-radio-button-wrapper:nth-child(4) {
	margin-top: -1px;
	margin-left: -1px;
	border-radius: 0 0 2px 0;
}

.paas-metric-title {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 4px;
	font-size: 12px;
}

.paas-metric-value {
	display: block;
	font-size: 20px;
}

.paas-metric-help-icon {
	color: rgba(0, 0, 0, 0.45);
	font-size: 12px;
	cursor: help;
}

.paas-metric-button.ant-radio-button-wrapper-checked .paas-metric-help-icon {
	color: inherit;
}

.paas-list-filter-clear {
	align-self: center;
	padding-inline: 4px;
}

.paas-list-status-open {
	max-width: 100%;
	white-space: normal;
	height: auto;
}

.paas-list-status-open.new-paas-results {
	background-color: v-bind(colorSuccessDark);
	border-color: v-bind(colorSuccessDark);
}

.paas-list-status-open.new-paas-results:hover,
.paas-list-status-open.new-paas-results:focus {
	background-color: v-bind(colorSuccess);
	border-color: v-bind(colorSuccess);
}
</style>
